import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogActions, Button, Alert,
  TextField, MenuItem, Typography, Box, CircularProgress, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import axios from "../../../config/axiosConfig";
import EditarLicenciaDialog from "./EditarLicenciaDialog";
import { useAuth } from "../../../context/AuthContext";
import { Encabezado, Seccion, Rejilla, fieldSx, contenidoSx, pieSx, botonPrincipalSx } from "./dialogoUi";

const RAZONES = ["Vacación", "Permiso"];
const hoy = () => new Date().toISOString().slice(0, 10);
const isoFecha = (value) => {
  if (!value) return hoy();
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1950) return hoy();
  return String(value).slice(0, 10);
};

// Cuando viene de una solicitud (botón "Procesar permiso" en /permisos/solicitudes)
// el formulario arranca precargado con lo que el empleado ya pidió, en vez de vacío.
const formInicial = (solicitud) => solicitud
  ? {
      razon: solicitud.razon || "Vacación",
      fecha_ini: isoFecha(solicitud.fecha_ini),
      fecha_fin: isoFecha(solicitud.fecha_fin),
      cantidad: String(solicitud.dias ?? ""),
      observacion: solicitud.observacion || "",
    }
  : { razon: "Vacación", fecha_ini: hoy(), fecha_fin: hoy(), cantidad: "", observacion: "" };

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1950) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Quién puede editar las licencias ya emitidas: el rol admin, o el rol conta con el subrol cont (el servidor lo comprueba otra vez)
const puedeEditarLicencias = (user) => {
  const role = String(user?.role || "").trim().toLowerCase();
  const sub = String(user?.sub_rol || user?.subRol || "").trim().toLowerCase();
  return role === "admin" || (role === "conta" && sub === "cont");
};

const EmitirVacacionDialog = ({ open, onClose, empleado, solicitud, onEmitido }) => {
  const { user } = useAuth();
  const puedeEditar = puedeEditarLicencias(user);
  const [editarRow, setEditarRow] = useState(null);
  const [saldo, setSaldo] = useState(null);
  const [ultimas, setUltimas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => formInicial(solicitud));
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(() => {
    if (!empleado?.id) return;
    setLoading(true);
    setError("");
    axios.get(`/api/permisos/empleado/${empleado.id}/ultimas-licencias`)
      .then(({ data }) => {
        setSaldo(data?.data?.saldo ?? 0);
        setUltimas(data?.data?.ultimas || []);
      })
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar la información."))
      .finally(() => setLoading(false));
  }, [empleado?.id]);

  useEffect(() => {
    if (open) {
      setForm(formInicial(solicitud));
      cargar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cargar]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const handleImprimir = async (idLicencia) => {
    // Abrir la pestaña ya (en blanco) antes del await, en el mismo gesto de click —
    // si se abre después el navegador la bloquea como popup (mismo patrón que ConcluirOT.jsx).
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) pdfWindow.opener = null;
    try {
      const { data } = await axios.get(`/api/permisos/licencia/${idLicencia}/papeleta.pdf`, { responseType: "blob" });
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (pdfWindow) {
        pdfWindow.location.replace(pdfUrl);
      } else {
        window.open(pdfUrl, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (e) {
      if (pdfWindow) pdfWindow.close();
      setError(e?.response?.data?.error || "No se pudo generar la papeleta.");
    }
  };

  const puedeEmitir = form.razon && form.fecha_ini && form.fecha_fin && Number(form.cantidad) > 0 && !saving;

  const handleEmitir = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post(`/api/permisos/empleado/${empleado.id}/emitir-licencia`, {
        fecha_ini: form.fecha_ini,
        fecha_fin: form.fecha_fin,
        cantidad: Number(form.cantidad),
        razon: form.razon,
        observacion: form.observacion.trim(),
        id_solicitud: solicitud?.id,
      });
      setForm(formInicial(null));
      cargar();
      if (onEmitido) onEmitido();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al emitir el registro.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper">
      <Encabezado titulo="Vacación / permiso" subtitulo={`${empleado?.nombre || ""}${empleado?.cargo ? ` — ${empleado.cargo}` : ""}`} />
      <DialogContent sx={contenidoSx}>
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
        {solicitud && (
          <Alert severity="success" sx={{ py: 0 }}>
            Procesando la solicitud #{solicitud.id}: al emitir, pasa a "Emitido".
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>
        ) : (
          <>
            <Alert severity="info" sx={{ py: 0 }}>
              Saldo disponible: <strong>{saldo}</strong> día(s)
            </Alert>

            <Seccion titulo="Últimos permisos" sinCuerpo>
              {ultimas.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", p: 1.25 }}>No tiene permisos anteriores.</Typography>
              ) : (
                <Table size="small" sx={{ "& td, & th": { py: 0.3, px: 0.75, fontSize: "0.74rem" }, "& th": { fontWeight: 700, color: "text.secondary" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Petición</TableCell>
                      <TableCell>Razón</TableCell>
                      <TableCell>Inicio</TableCell>
                      <TableCell>Fin</TableCell>
                      <TableCell align="center">Días</TableCell>
                      <TableCell>Observación</TableCell>
                      <TableCell align="center">Imprimir</TableCell>
                      {puedeEditar && <TableCell align="center">Editar</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ultimas.map((r, idx) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{formatFecha(r.fecha_peticion)}</TableCell>
                        <TableCell>{r.razon}</TableCell>
                        <TableCell>{formatFecha(r.fecha_ini)}</TableCell>
                        <TableCell>{formatFecha(r.fecha_fin)}</TableCell>
                        <TableCell align="center">{r.cantidad}</TableCell>
                        <TableCell>{r.observacion || "-"}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleImprimir(r.id)} title="Imprimir papeleta">
                            <PrintIcon fontSize="inherit" />
                          </IconButton>
                        </TableCell>
                        {puedeEditar && (
                          <TableCell align="center">
                            {/* La lista va de la más reciente a la más antigua: solo se pueden editar las 3 últimas */}
                            {idx < 3 ? (
                              <IconButton size="small" onClick={() => setEditarRow(r)} title="Editar vacación o permiso">
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            ) : (
                              <Typography component="span" sx={{ color: "text.disabled" }} title="Solo se pueden editar las 3 últimas">—</Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Seccion>

            <Seccion titulo="Emitir nuevo">
              <Rejilla cols={2}>
                <TextField select size="small" label="Razón" value={form.razon} onChange={handleChange("razon")} sx={fieldSx}>
                  {RAZONES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Cantidad de días" value={form.cantidad} onChange={handleChange("cantidad")} sx={fieldSx} />
                <TextField size="small" type="date" label="Fecha inicio" InputLabelProps={{ shrink: true }} value={form.fecha_ini} onChange={handleChange("fecha_ini")} sx={fieldSx} />
                <TextField size="small" type="date" label="Fecha fin" InputLabelProps={{ shrink: true }} value={form.fecha_fin} onChange={handleChange("fecha_fin")} sx={fieldSx} />
              </Rejilla>
              <TextField size="small" label="Observación" multiline minRows={2} fullWidth value={form.observacion} onChange={handleChange("observacion")} sx={{ ...fieldSx, mt: 1 }} />
            </Seccion>
          </>
        )}
      </DialogContent>
      <EditarLicenciaDialog
        open={Boolean(editarRow)}
        registro={editarRow}
        onClose={() => setEditarRow(null)}
        onGuardado={() => { setEditarRow(null); cargar(); if (onEmitido) onEmitido(); }}
      />
      <DialogActions sx={pieSx}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cerrar</Button>
        <Button variant="contained" onClick={handleEmitir} disabled={!puedeEmitir || loading} sx={botonPrincipalSx}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Emitir"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmitirVacacionDialog;
